import { useState, useMemo } from "react";
import { useI18n } from "../../lib/i18n.jsx";
import { useTrips } from "../../lib/useTrips.js";
import TripCard from "../TripCard.jsx";
import Reveal from "../Reveal.jsx";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Check, Sun, Snowflake, Loader2 } from "lucide-react";

export default function Pricing() {
  const { t, lang } = useI18n();
  const [tab, setTab] = useState("summer");
  const { trips, loading, error } = useTrips();

  const summer = useMemo(() => trips.filter((x) => x.season === "summer"), [trips]);
  const winter = useMemo(() => trips.filter((x) => x.season === "winter"), [trips]);

  // Year label = year of the FIRST UPCOMING trip in this season.
  // No fallback to past trips — if nothing is upcoming, just show the season
  // name without a year (avoids stale "Lato 2026" after summer ended).
  const buildLabel = (list, label) => {
    const upcoming = list
      .filter((t) => !t.is_past)
      .sort((a, b) => a.date_start.localeCompare(b.date_start));
    const year = upcoming[0]?.date_start?.slice(0, 4);
    return year ? `${label} ${year}` : label;
  };
  const summerLabel = useMemo(
    () => buildLabel(summer, { pl: "Lato", uk: "Літо", en: "Summer" }[lang] || "Summer"),
    [summer, lang]
  );
  const winterLabel = useMemo(
    () => buildLabel(winter, { pl: "Zima", uk: "Зима", en: "Winter" }[lang] || "Winter"),
    [winter, lang]
  );

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-white py-20 md:py-28"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-10">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.pricing.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.pricing.title}
          </h2>
          <p className="text-lg text-winter/65">{t.pricing.subtitle}</p>
        </Reveal>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-paper p-1.5 mb-10 mx-auto rounded-full h-auto flex" data-testid="pricing-tabs">
            <TabsTrigger
              value="summer"
              data-testid="pricing-tab-summer"
              className="font-semibold text-base px-6 py-2.5 rounded-full data-[state=active]:bg-summer data-[state=active]:text-cream data-[state=active]:shadow-md flex items-center gap-2"
            >
              <Sun className="w-4 h-4" /> {summerLabel}
            </TabsTrigger>
            <TabsTrigger
              value="winter"
              data-testid="pricing-tab-winter"
              className="font-semibold text-base px-6 py-2.5 rounded-full data-[state=active]:bg-winter data-[state=active]:text-cream data-[state=active]:shadow-md flex items-center gap-2"
            >
              <Snowflake className="w-4 h-4" /> {winterLabel}
            </TabsTrigger>
          </TabsList>

          {loading && (
            <div className="flex items-center justify-center py-10 text-winter/60">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t.pricing.loading}
            </div>
          )}
          {error && !loading && (
            <p className="text-center text-destructive">{t.pricing.error}</p>
          )}

          {!loading && !error && (
            <>
              <TabsContent value="summer">
                {summer.length === 0 ? (
                  <p className="text-center text-winter/60 py-10">{t.pricing.empty}</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {summer.map((trip, i) => (
                      <Reveal
                        key={trip.shortcode}
                        variant="scale"
                        delay={Math.min(i * 70, 420)}
                      >
                        <TripCard trip={trip} />
                      </Reveal>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="winter">
                {winter.length === 0 ? (
                  <p className="text-center text-winter/60 py-10">{t.pricing.empty}</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {winter.map((trip, i) => (
                      <Reveal
                        key={trip.shortcode}
                        variant="scale"
                        delay={Math.min(i * 70, 420)}
                      >
                        <TripCard trip={trip} />
                      </Reveal>
                    ))}
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Includes */}
        <div className="mt-12 card-soft p-8 md:p-10">
          <p className="font-handwritten text-summer text-xl md:text-2xl mb-2">
            {t.pricing.includes}
          </p>
          <h3 className="font-display font-bold text-winter text-2xl md:text-3xl mb-6">
            {t.pricing.includes_title}
          </h3>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
            {t.pricing.includes_items.map((item, i) => (
              <div key={i} className="flex items-start gap-3 py-1.5">
                <div className="shrink-0 w-6 h-6 rounded-full bg-summer/10 flex items-center justify-center mt-0.5">
                  <Check className="w-3.5 h-3.5 text-summer" strokeWidth={3} />
                </div>
                <span className="text-winter/85">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
